import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Token de color de la paleta (ej. `"multiva"`, `"peligro"`, `"exito"`,
   * `"slate"`) · resuelve las 3 variables de abajo por `var()`, igual razón
   * que en `Text`/`IconButton` (el color casi siempre es dinámico —
   * `EstadoBadge` lo arma desde una tabla de ~30 estados).
   */
  color?: string;
  variant?: "light" | "filled" | "outline";
  size?: "xs" | "sm" | "md";
  /** Igual que Mantine: MAYÚSCULAS por default (convención de badges del portal). */
  uppercase?: boolean;
}

const SIZE_CLASS = {
  xs: "h-4 px-1.5 text-[10px]",
  sm: "h-5 px-2 text-xs",
  md: "h-6 px-2.5 text-xs",
} as const;

/**
 * Reemplazo de `<Badge>` de Mantine. El `variant="light"` (default, el más
 * usado del repo — 78 de 57 usos) porta 1:1 la función `styles` custom de
 * `mantine-theme.ts`: fondo pastel (shade 0) + texto oscuro (shade 8) en
 * claro, y `color-mix` del shade 6 al 20% + texto shade 2 en oscuro — NO el
 * `variant="light"` genérico de Mantine (que satura shade 6 de fondo Y
 * texto, se ve "semáforo"). Contraste verificado 5.8–10.9:1 en claro.
 */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { color = "multiva", variant = "light", size = "sm", uppercase = true, className, style, children, ...props },
    ref
  ) => {
    const vars =
      variant === "light"
        ? {
            backgroundColor: `light-dark(var(--color-${color}-50), color-mix(in srgb, var(--color-${color}-600) 20%, transparent))`,
            color: `light-dark(var(--color-${color}-800), var(--color-${color}-200))`,
            borderColor: `light-dark(var(--color-${color}-200), color-mix(in srgb, var(--color-${color}-400) 35%, transparent))`,
          }
        : variant === "filled"
          ? {
              backgroundColor: `var(--color-${color}-600)`,
              color: "#fff",
              borderColor: `var(--color-${color}-600)`,
            }
          : {
              // outline
              backgroundColor: "transparent",
              color: `var(--color-${color}-600)`,
              borderColor: `var(--color-${color}-600)`,
            };
    return (
      <span
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-sm border font-semibold leading-none",
          SIZE_CLASS[size],
          uppercase && "uppercase tracking-wide",
          className
        )}
        style={{ ...vars, ...style }}
        {...props}
      >
        {children}
      </span>
    );
  }
);
Badge.displayName = "Badge";
